/**
 * Theme: "Teknik Resim" (light) and "Mavi Kopya" (dark). "Sistem" follows
 * prefers-color-scheme; "Açık" and "Koyu" set data-theme on <html>, which overrides it.
 * The choice is a per-device convenience kept in localStorage.
 */

import { useSyncExternalStore } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'twin-theme';
export const THEME_COLORS = { light: '#F4F2EC', dark: '#0D1624' } as const;

const THEME_EVENT = 'twin:theme';

export function readThemePreference(): ThemePreference {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** The theme actually on screen. */
export function resolvedTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

/** Sets data-theme and the browser chrome colour. */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
  const color = THEME_COLORS[resolvedTheme(preference)];
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    if (preference === 'system') {
      meta.content = meta.media.includes('dark') ? THEME_COLORS.dark : THEME_COLORS.light;
    } else {
      meta.content = color;
    }
  }
}

export function setThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage can be blocked; the theme still applies for this session.
  }
  applyTheme(preference);
  window.dispatchEvent(new Event(THEME_EVENT));
}

/** Cycles Sistem, Açık, Koyu for the sidebar button. */
export function nextThemePreference(preference: ThemePreference): ThemePreference {
  return preference === 'system' ? 'light' : preference === 'light' ? 'dark' : 'system';
}

export const THEME_LABELS: Record<ThemePreference, string> = { system: 'Sistem', light: 'Açık', dark: 'Koyu' };

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(
    (listener) => {
      window.addEventListener(THEME_EVENT, listener);
      window.addEventListener('storage', listener);
      return () => {
        window.removeEventListener(THEME_EVENT, listener);
        window.removeEventListener('storage', listener);
      };
    },
    readThemePreference,
    () => 'system',
  );
}
